"use client";

/**
 * MOBILE HERO — the same {@link HeroProductWindow} as desktop, uniformly scaled
 * to the phone column (~1124px → ~0.3×, prototype zoom:0.3), on the same dot
 * grid + hairline ring as {@link HeroAppWindow}.
 */
import { ld } from "@/lib/theme/light-dark";
import type { ReactNode } from "react";

import { MARKETING_PRODUCT_WINDOW_LABEL } from "@/lib/marketing-seo";
import { DotGridBackdrop } from "../dot-grid-backdrop";
import {
  HeroProductWindow,
  type HeroProductWindowProps,
} from "./app-window/hero-product-window";
import { MODULE_REGISTRY } from "./app-window/module-registry";
import { ResponsiveScale } from "./hero-mobile-scale";
import {
  MOBILE_HERO_GUTTER,
  WINDOW_CHROME_BG,
  WINDOW_RING,
} from "./hero-tokens";

/** Desktop inner width (1180 frame − 2×28 padding) — matches prototype zoom layer. */
const SIM_BASE_WIDTH = 1124;

/** Widest the window gets on screen, and its corner radius there. */
const WINDOW_MAX_WIDTH = 340;
const WINDOW_RADIUS = 9;

/** The scale the window runs at once {@link ResponsiveScale} has fitted it. */
const SIM_SCALE = WINDOW_MAX_WIDTH / SIM_BASE_WIDTH;

/**
 * Window radius in BASE px so it lands on {@link WINDOW_RADIUS} once scaled.
 * The scaler does not clip (Inbox callouts hang outside the window edge, as on
 * desktop), so the window itself carries the corner rounding.
 */
const MOBILE_WINDOW_RADIUS = Math.round(WINDOW_RADIUS / SIM_SCALE);

export function HeroMobileWindow(props: HeroProductWindowProps): ReactNode {
  const bleedPx = MODULE_REGISTRY[props.active].mobileOverlayBleedPx ?? 0;

  return (
    <section
      id="produkt"
      aria-label={MARKETING_PRODUCT_WINDOW_LABEL}
      style={{
        position: "relative",
        marginTop: 26,
        padding: `0 ${MOBILE_HERO_GUTTER}px`,
        display: "flex",
        justifyContent: "center",
      }}
    >
      <DotGridBackdrop
        tone="light"
        focus="center"
        style={{ top: -36, bottom: -28 }}
      />
      <div
        className="mkhero-brand"
        style={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: WINDOW_MAX_WIDTH,
          borderRadius: WINDOW_RADIUS,
          background: WINDOW_CHROME_BG,
          boxShadow: `${WINDOW_RING}, 0 30px 60px -34px ${ld("rgba(11,15,25,0.30)", "rgba(0,0,0,0.5)")}`,
        }}
      >
        <ResponsiveScale baseWidth={SIM_BASE_WIDTH} clip={false}>
          <div key={props.active} style={{ width: SIM_BASE_WIDTH }}>
            <HeroProductWindow
              {...props}
              mobileOverlayBleedPx={bleedPx}
              windowRadiusPx={MOBILE_WINDOW_RADIUS}
            />
          </div>
        </ResponsiveScale>
      </div>
    </section>
  );
}
